import S, { DataSignal } from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import { values } from 'underscore';
import { IAjax } from 'lib/Ajax';
import { AutoSavingDraftObject, DraftAutoSaver } from 'lib/autoSave';
import { DraftObject, mergeDraftMethods } from 'lib/draftSignals';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { tzmoment, tzmomentMod } from 'lib/tzmoment';
import { NullableConvertibleMeasurement } from 'lib/ConvertibleMeasurement';
import { ConvertedMeasurement } from 'lib/ConvertedMeasurement';
import { MeasurementInFeetAndInches } from 'lib/MeasurementInFeetAndInches';
import UOM from 'lib/UOM';
import { CaseInsensitiveStartsWith } from 'lib/diacriticals';
import { jsonableObservable } from 'lib/jsonableObservable';
import { ValidationWatcher, mergeErrors, parseModelStateErrors } from 'lib/validationWatcher';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { Berth } from 'data/models/Berth';
import { BerthReservation } from 'data/models/BerthReservation';
import { BowThruster } from 'data/models/BowThruster';
import { CancelPilotJobOrderResultData } from 'data/models/CancelPilotJobOrderResult';
import { CancelPortCallRequestOptions } from 'data/models/CancelPortCallRequest';
import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { DispatchDashboardCorePermissions } from 'data/models/DispatchDashboard';
import { EffectiveDate } from 'data/models/ScheduleDate';
import { IssueSeverity } from 'server/enums/Issue';
import { IssueType } from 'data/models/IssueType';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { Measurement, NullableMeasurement } from 'data/models/Measurement';
import { PCUser } from 'data/models/PCUser';
import { PhoneNumberKind } from 'server/enums/PhoneNumberKind';
import { PierSettings } from 'data/models/PierSettings';
import { PilotJobOrder, PilotJobOrderData, EffectiveLength, EffectiveAirDraft, EffectiveDeepDraft, EffectiveDraft } from 'data/models/PilotJobOrder';
import { PilotJobOrderCancellationOptions } from 'data/models/PilotJobOrderCancellation';
import { PilotJobOrderDetails as ServerPilotJobOrderDetails } from 'data/models/PilotJobOrderDetails';
import { PilotShiphandlingNote } from 'data/models/PilotShiphandlingNote';
import { PortCall } from 'data/models/PortCall';
import { Vessel } from 'data/models/Vessel';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { agentRestrictionErrors } from 'common/services/agentRestrictionsErrorParser';

import { EditMovementEndPopup, EditMovementEndPopupParams } from 'portcall/editmovementendpopup/model';
import { AddShiftPopup, AddShiftPopupParams } from 'pilots/addshiftpopup/model';
import { DateTimeEditPopup, NullableDateTimeEditPopup, DateTimeEditPopupDismissStatus } from 'portcall/datetimeeditpopup/model';
import { NullableFeetInchesMetersEdit } from 'portcall/feetinchesmetersedit/model';
import { FootmarkAssignments, FootmarkAssignmentsParams, FootmarksUpdateTrigger } from 'portcall/footmarkassignments/model';
import { PilotJobOrderLogUpdater, PilotJobOrderLogUpdaterParams } from 'pilots/pilotjoborderlog/logupdater';
import { PilotJobOrderLog, PilotJobOrderLogParams } from 'pilots/pilotjoborderlog/model';
import { PilotJobOrderPreferences, PilotJobOrderPreferencesParams } from 'pilots/dispatchdashboard/pilotjoborderpreferences';
import { Typeahead } from 'portcall/typeahead/model';

import { PilotAssignments, PilotAssignmentsParams } from './pilotassignments';
import { PilotageCharges, PilotageChargesParams } from './pilotagecharges';
import { OriginalPilotOrder, OriginalPilotOrderParams } from './originalpilotorder';

export const enum BackPage {
    Log = 0,
    ShiphandlingNotes = 1,
    Vessel = 2,
    PilotageCharges = 3
}

export interface PilotJobOrderDetailsParams {
    pjo : PilotJobOrder;
    model : ServerPilotJobOrderDetails;
    flip : BackPage | null;
    onDelete : () => void;
    onReorder : () => void;
    // TH 04/13/2017 -- NOTE: onAddShift gets called w/ the new PJO that was added,
    // not the PJO that was open at the time.
    onAddShift : (pjo : PilotJobOrder) => void;
    onAcknowledge : () => void;
    canAcknowledge : () => boolean;
    corePermissions : () => DispatchDashboardCorePermissions | null;
}

export function PilotJobOrderDetails(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    tzutils : TZUtils,
    localization : LocalizationContext,
    pierSettings : PierSettings,
    user : () => PCUser | null,
    pjoPrefsModel : (params : PilotJobOrderPreferencesParams) => PilotJobOrderPreferences,
    pilotAssignmentsModel : (params : PilotAssignmentsParams) => PilotAssignments,
    logUpdaterModel : (params : PilotJobOrderLogUpdaterParams) => PilotJobOrderLogUpdater,
    pilotJobOrderLogModel : (params : PilotJobOrderLogParams) => PilotJobOrderLog,
    addShiftPopupModel : (params : AddShiftPopupParams) => AddShiftPopup,
    editMovementEndPopupModel : (params : EditMovementEndPopupParams) => EditMovementEndPopup,
    pilotageChargesModel : (params : PilotageChargesParams) => PilotageCharges,
    originalPilotOrderModel : (params : OriginalPilotOrderParams) => OriginalPilotOrder,
    footmarkAssignmentsModel : (params : FootmarkAssignmentsParams) => FootmarkAssignments,
    {
        pjo,
        model,
        flip,
        onDelete,
        onReorder,
        onAddShift,
        onAcknowledge,
        canAcknowledge,
        corePermissions
    } : PilotJobOrderDetailsParams
) {
    const
        showActionsMenu = S.data(false),
        actionToConfirm = S.data<(() => void) | null>(null),
        flipped = S.data(flip !== null),
        backPage = S.data<BackPage | null>(flip),
        clock = tzutils.clock(1000),
        bowThrusterModel = (b : BowThruster) => BowThrusterViewModel(b, model),
        vesselModel = (v : Vessel) => VesselViewModel(v, ajax, pierSettings, bowThrusterModel),
        portCallModel = (pc : PortCall) => PortCallViewModel(pc, ajax, AgentViewModel),
        reservationModel = (br : BerthReservation) => ReservationViewModel(br, () => model.CanEditPortCall() && !!model.PilotJobOrder() && !model.PilotJobOrder()!.Cancellation(), footmarkAssignmentsModel),
        // Th 02/03/2017 -- Berths for berth typeaheds
        berths = lift(() => Array.from(data.Berths.items()))
            .filter(b =>
                !!b.Location() &&
                !!pjo.EffectivePortCall() &&
                b.Location()!.PortId() === pjo.EffectivePortCall()!.PortId()),
        itineraryPointModel = (pt : ItineraryPoint) => ItineraryPointViewModel(pt, ajax, data, berths, reservationModel),
        jobOrder = PilotJobOrderViewModel({
            pjo,
            ajax,
            entrypoints,
            data,
            pierSettings,
            tzutils,
            actionToConfirm,
            portCallModel,
            itineraryPointModel,
            vesselModel,
            pjoPrefsModel,
            addShiftPopupModel,
            editMovementEndPopupModel,
            onDelete,
            onReorder,
            onAddShift,
            onAcknowledge
        }),
        berthReservationDetails = BerthReservationDetailsSection(jobOrder),
        pilotAssignments = pilotAssignmentsModel({ model }),
        logUpdater = logUpdaterModel({ pjo }),
        log = pilotJobOrderLogModel({ pjo }),
        originalPilotOrder = originalPilotOrderModel({ pjo, hideActionsMenu: () => showActionsMenu(false) }),
        Validation = S(() => mergeErrors(jobOrder.FromPoint()?.Validation() ?? {}, jobOrder.ToPoint()?.Validation() ?? {})),
        AgentRestrictions = S(() => agentRestrictionErrors(Validation())),
        ShowAgentRestrictions = S.value(false);

    S(() => ShowAgentRestrictions(Object.keys(AgentRestrictions()).length > 0));

    S(() => {
        // No need to poll PortCall Log when the Log isn't visible.
        if (flipped() && backPage() === BackPage.Log) logUpdater.start();
        else logUpdater.stop();
    });

    // Standard dropdown options used for BowThruster Present and Does it work? fields
    const yesNoUnknown = [{
        name: '--',
        value: null
    }, {
        name: 'Yes',
        value: true
    }, {
        name: 'No',
        value: false
    }] as { name: string, value : boolean | null }[];

    const pilotageCharges = pilotageChargesModel({
        PJO: jobOrder,
        PersistedPJO: pjo,
        PilotageChargesFlipActive: () => flipped() && backPage() === BackPage.PilotageCharges
    });

    return {
        Model: model,
        JobOrder: jobOrder,
        // TH 04/13/2017 -- For Acknowledgement workflow:
        // Preload the Acknowledge permission from the DDash.
        // Show the Acknowledge link if either the DDash or the PJO Details response indicate that they have permissions.
        // That way we can make the Acknowledge link available even before the rest of the PJO details loads.
        // This speeds up the workflow for users acknowledging several PJOs in a row.
        CanAcknowledge: () => canAcknowledge() || model.CanAcknowledge(),
        CanReorder: () => corePermissions()?.CanAddPilotJobOrders() ?? null,
        ShowConflicts,
        ShowActionsMenu: showActionsMenu,
        ActionToConfirm: actionToConfirm,
        ToggleActionsMenu: () => showActionsMenu(!showActionsMenu()),
        PilotAssignments: pilotAssignments,
        PilotageCharges: pilotageCharges,
        OriginalPilotOrder: originalPilotOrder,
        BerthReservationDetails: berthReservationDetails,
        Log: log,
        Flipped: flipped,
        MenuAction,
        FlipToBack,
        BackPage: backPage,
        YesNoUnknown: yesNoUnknown,
        Localization: localization,
        TugCompanies: lift(() => pjo.PilotAgency()?.TugCompanies() ?? [])
            .filter(tug => tug.Active()),
        LineHandlingCompanies: lift(() => pjo.PilotAgency()?.LineHandlingCompanies() ?? [])
            .filter(lh => lh.Active()),
        Clock: clock,
        User: user,
        // Signal to the DDash that we'd like to take over the entire page w/ temporary UI of our
        // choosing. In theory, there could be multiple reasons we want to do this. In practice,
        // we'll use this to support printing the original pilot order. We want that UI to briefly
        // be the only thing visible on screen.
        IsRequestingFullPageTakeOver: () =>
            !!originalPilotOrder.DisplayedOrder(),
        AgentRestrictions,
        ShowAgentRestrictions
    };

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    function FlipToBack(page : BackPage) {
        S.freeze(() => {
            backPage(page);
            flipped(true);
        });
    }

    function ShowConflicts() {
        // Ignore conflicts for CNX PJOs
        return !pjo.Cancellation();
    }
}
export type PilotJobOrderDetails = ReturnType<typeof PilotJobOrderDetails>;

function PilotJobOrderViewModel({
    pjo,
    ajax,
    entrypoints,
    data,
    pierSettings,
    tzutils,
    actionToConfirm,
    portCallModel,
    itineraryPointModel,
    vesselModel,
    pjoPrefsModel,
    addShiftPopupModel,
    editMovementEndPopupModel,
    onDelete,
    onReorder,
    onAddShift,
    onAcknowledge
} : {
    pjo : PilotJobOrder,
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    pierSettings : PierSettings,
    tzutils : TZUtils,
    actionToConfirm : DataSignal<(() => void) | null>,
    portCallModel : (pc : PortCall) => PortCallViewModel,
    itineraryPointModel : (pt : ItineraryPoint) => ItineraryPointViewModel,
    vesselModel : (v : Vessel) => VesselViewModel,
    pjoPrefsModel : (params : PilotJobOrderPreferencesParams) => PilotJobOrderPreferences,
    addShiftPopupModel : (params : AddShiftPopupParams) => AddShiftPopup,
    editMovementEndPopupModel : (params : EditMovementEndPopupParams) => EditMovementEndPopup,
    onDelete : () => void,
    onReorder: () => void,
    onAddShift : (_pjo : PilotJobOrder) => void,
    onAcknowledge : () => void
}) {
    const
        client = ajax.Url<PilotJobOrder, {}>(() => pjo.Url() || '', { useCache: true }),
        cnxClient = ajax.Url<PilotJobOrderCancellationOptions, CancelPilotJobOrderResultData>(() => entrypoints.CancelPilotJobOrder({ Id: pjo.Id() })),
        draft = AutoSavingDraftObject(pjo, client, [
            'AirDraft.Num',
            'AirDraft.UOM',
            'Billable',
            'BillToAddress',
            'BowThrusterOperational',
            'DeepDraftConfirmed',
            'DeepDraft.Num',
            'DeepDraft.UOM',
            'End.Actual',
            'End.Estimated',
            'LaborStartTime',
            'Length.Num',
            'Length.UOM',
            'LineHandlingCompanyId',
            'NumberOfTugs',
            'QBTracking.Customer',
            'Remarks',
            'Start.Actual',
            'Start.Estimated',
            'StartTimeConfirmed',
            'TugCompanyId',
            'VesselId'
        ]),
        transitTime = () => tzmoment(draft.End.Estimated()).diff(tzmoment(draft.Start.Estimated()), 'minutes'),
        showUndoStartActual = S.data(false),
        showUndoEndActual   = S.data(false),
        syncedStartAndEndEstimate = (val? : TZDate) => {
            if (val === undefined) return draft.Start.Estimated();
            return S.freeze(() => {
                draft.End.Estimated(tzmomentMod(tzutils, val, m => m.add(transitTime(), 'minutes')));
                return draft.Start.Estimated(val);
            });
        },
        showEditMovementEndPopup = S.data(false),
        editMovementEndPopup = S(() => {
            if (!showEditMovementEndPopup()) return null;

            const
                toRes   = pjo.EffectiveToPoint  ()?.BerthReservation(),
                fromRes = pjo.EffectiveFromPoint()?.BerthReservation(),
                validateClient = ajax.Url<PilotJobOrderData, {}>(entrypoints.ValidatePilotJobOrderTimes);

            return editMovementEndPopupModel({
                EndTime: draft.End.Estimated,
                StartTime: draft.Start.Estimated,
                MovementType : fromRes ? toRes ? 'shift' : 'out' : 'in',
                OnClosePopup: () => showEditMovementEndPopup(false),
                Validate: (startDate, endDate) => {
                    const pjo = JSON.parse(JSON.stringify(draft)) as PilotJobOrderData;
                    pjo.Start.Estimated = startDate.toJSON();
                    pjo.End.Estimated = endDate.toJSON();
                    return validateClient.Put(pjo).then(
                        () => [],
                        errs => values(parseModelStateErrors(errs))
                    );
                }
            });
        }),
        showDateTimeEditPopup = S.data<'start-est' | 'start-effective' | 'end-effective' | null>(null),
        dateTimeEditPopup = S(() => {
            if (showDateTimeEditPopup() === null) return null;
            const
                popupKind = showDateTimeEditPopup(),
                value =
                    popupKind === 'start-est' ? syncedStartAndEndEstimate :
                    popupKind === 'start-effective' ? draft.Start.Effective :
                    popupKind === 'end-effective' ? draft.End.Effective :
                    // Until typescript lands throw expressions...
                    // See https://github.com/Microsoft/TypeScript/issues/18535
                    (() => { throw new Error(`Unknown dateTimeEditPopup kind: ${popupKind}`); })(),
                validateClient = ajax.Url<PilotJobOrderData, {}>(entrypoints.ValidatePilotJobOrderTimes),
                validation = ValidationWatcher(validateClient);

            return DateTimeEditPopup({
                value,
                onDismiss: () => showDateTimeEditPopup(null),
                validation: {
                    validate: val => {
                        const toValidate = JSON.parse(JSON.stringify(draft)) as PilotJobOrderData;
                        if (popupKind === 'start-est') {
                            toValidate.Start.Estimated = val.toJSON();
                            toValidate.End.Estimated = tzmomentMod(tzutils, val, m => m.add(transitTime(), 'minutes')).toJSON();
                        } else if (popupKind === 'start-effective') {
                            if (toValidate.Start.Actual) toValidate.Start.Actual = val.toJSON();
                            else toValidate.Start.Estimated = val.toJSON();
                        } else if (popupKind === 'end-effective') {
                            if (toValidate.End.Actual) toValidate.End.Actual = val.toJSON();
                            else toValidate.End.Estimated = val.toJSON();
                        }
                        return validateClient.Put(toValidate);
                    },
                    errors: () => values(validation())
                }
            });
        }),
        showNullableDateTimeEditPopup = S.data<'labor-start' | null>(null),
        nullableDateTimeEditPopup = S(() => {
            if (showNullableDateTimeEditPopup() === null) return null;
            const value =
                showNullableDateTimeEditPopup() === 'labor-start' ? draft.LaborStartTime :
                // Until typescript lands throw expressions...
                (() => { throw new Error(`Unknown nullableDateTimeEditPopup kind: ${showNullableDateTimeEditPopup()}`); })();

            return NullableDateTimeEditPopup({
                value,
                onDismiss: () => showNullableDateTimeEditPopup(null)
            });
        }),
        showLogActualTimePopup = S.data<'start-actual' | 'end-actual' | null>(null),
        logActualTimePopup = S(() => {
            if (showLogActualTimePopup() === null) return null;
            const
                now = tzutils.now(),
                popupKind = showLogActualTimePopup(),
                actual =
                    popupKind === 'start-actual' ? draft.Start.Actual :
                    popupKind === 'end-actual'   ? draft.End.Actual :
                    // Until typescript lands throw expressions...
                    (() => { throw new Error(`Unknown logActualTimePopup kind: ${popupKind}`); })(),
                validateClient = ajax.Url<PilotJobOrderData, {}>(entrypoints.ValidatePilotJobOrderTimes),
                validation = ValidationWatcher(validateClient);

            return DateTimeEditPopup({
                value: (val? : TZDate) => {
                    if (val === undefined) return actual() || now;
                    return actual(val)!;
                },
                onDismiss: status => S.freeze(() => {
                    showLogActualTimePopup(null);
                    // If the user hit OK without editing the value in the DateTimeEditPopup,
                    // we wanna set the actual to the init value at the time the popup is dismissed.
                    if (status === DateTimeEditPopupDismissStatus.OKNotEdited) actual(now);
                }),
                validation: {
                    validate: val => {
                        const toValidate = JSON.parse(JSON.stringify(draft)) as PilotJobOrderData;
                        if (popupKind === 'start-actual') {
                            toValidate.Start.Actual = val.toJSON();
                        } else if (popupKind === 'end-actual') {
                            toValidate.End.Actual = val.toJSON();
                        }
                        return validateClient.Put(toValidate);
                    },
                    // We initialize the popup value to now, which might be invalid if
                    // PJO start is somehow in the future. Hence we validate immediately.
                    runOnStart: true,
                    errors: () => values(validation())
                }
            });
        }),
        showAddShift = S.data(false),
        addShiftPopup = S(() => showAddShift() ? addShiftPopupModel({
            // TH 02/13/2017 -- We'll only ever show the AddShiftPopup for OUTs, in which
            // case we want to use the FromPoint as the previousPoint.
            previousPoint : pjo.FromPoint,
            onCancel: () => showAddShift(false),
            onSuccess: AddShiftSuccess
        }) : null),
        showCancellationRemarksPromp = S.value(false),
        cancellationRemarksPrompt = S(() => showCancellationRemarksPromp() ?
            CancellationRemarksPrompt(Cancel, () => S.freeze(() => {
                showCancellationRemarksPromp(false);
                actionToConfirm(null);
            })) :
            null
        ),
        showAbortRemarksPromp = S.value(false),
        abortRemarksPrompt = S(() => showAbortRemarksPromp() ?
            AbortRemarksPrompt(Cancel, UndoStartActual, () => S.freeze(() => {
                    showAbortRemarksPromp(false);
                    actionToConfirm(null);
            })) :
            null
        ),
        cancelWholePortCallParams = S.value<{ remarks : string | null} | null>(null),
        cancelWholePortCallPrompt = S(() => cancelWholePortCallParams() ? CancelWholePortCallPrompt({
            pjo,
            remarks: cancelWholePortCallParams()!.remarks,
            ajax,
            entrypoints,
            hidePrompt: () => cancelWholePortCallParams(null)
        }) : null),
        { draftPreferences } = pjoPrefsModel({ pjo }),
        Working = S.value(false);

    // TODO -- Possibly move lock/unlock logic server-side. I spotted about 5 cases in CHP db where
    // PJOs had logged pilot aboard but didn't have a locked vessel. My guess is those are coming
    // via the homepage movements widget.
    // TH 02/03/2017 -- Wire up draft computed and navigation properties.
    const startActualDraftSignal = draft.Start.Actual;
    draft.Start.Actual = jsonableObservable((val? : TZDate | null) => {
        if (val === undefined) return startActualDraftSignal();
        return S.sample(() => S.freeze(() => {
            // TH 03/09/2017 -- Memorialize the current PortCall Vessel and Length when the user
            // logs pilot aboard...
            if (val) {
                // ... but do nothing if the user is just changing the pilot aboard time...
                if (draft.EffectivePortCall() && !startActualDraftSignal()) {
                    draft.VesselId(draft.EffectivePortCall()!.VesselId());
                    draft.Length.set(draft.EffectivePortCall()!.VesselLength);
                }
            } else {
                // ... and if undoing pilot aboard, default back to current PortCall Vessel and Length.
                draft.VesselId(null);
                draft.Length.set(new NullableMeasurement());
            }
            return startActualDraftSignal(val);
        }));
    });
    draft.Start.Effective = EffectiveDate(draft.Start);
    draft.End.Effective   = EffectiveDate(draft.End);
    draft.EffectiveLength = EffectiveLength(draft);
    draft.EffectiveAirDraft  = EffectiveAirDraft(draft);
    draft.EffectiveDeepDraft = EffectiveDeepDraft(draft);
    draft.TugCompany          = S.value(S.sample(pjo.TugCompany));
    draft.LineHandlingCompany = S.value(S.sample(pjo.LineHandlingCompany));
    draft.Vessel              = S.value(S.sample(pjo.Vessel));
    data.PilotJobOrders.attach(draft, [draft.TugCompany, draft.LineHandlingCompany, draft.Vessel]);

    // TH 02/03/2017 -- Have to construct these AFTER setting draft.EffectiveAirDraft and draft.EffectiveDeepDraft above.
    const
        effectiveDeepDraft        = EffectiveDraftViewModel(draft.DeepDraft, draft.EffectiveDeepDraft),
        effectiveAirDraft         = EffectiveDraftViewModel(draft.AirDraft,  draft.EffectiveAirDraft),
        deepDraftConfirmed = ((val? : boolean) => {
            if (val === undefined) return draft.DeepDraftConfirmed();
            return S.freeze(() => {
                if (val) {
                    // TH: 02/02/2017 -- "Lock" PJO Air and DeepDraft upon confirming DeepDraft.
                    // That way future changes to the PortCall drafts don't alter the confirmed draft value.
                    effectiveDeepDraft.Lock();
                    effectiveAirDraft.Lock();
                }
                return draft.DeepDraftConfirmed(val);
            });
        }) as DataSignal<boolean>,
        hasDraftConflict = pjo.Issues.some(
            i => i.Type() === IssueType.PilotJobOrderExceedsBerthDepth && i.Severity() >= IssueSeverity.Conflict
        );

    return {
        Model: draft,
        PortCall: S(() => pjo.EffectivePortCall() && portCallModel(pjo.EffectivePortCall()!)),
        FromPoint: S(() => pjo.EffectiveFromPoint() && itineraryPointModel(pjo.EffectiveFromPoint()!)),
        ToPoint: S(() => pjo.EffectiveToPoint() && itineraryPointModel(pjo.EffectiveToPoint()!)),
        EffectiveVessel: S(() => pjo.EffectiveVessel() && vesselModel(pjo.EffectiveVessel()!)),
        EffectiveLength: () => {
            const effective = new Measurement({
                Num: draft.EffectiveLength.Num(),
                UOM: draft.EffectiveLength.UOM()
            });
            return lengthText(effective as NullableMeasurement, pierSettings.DefaultLengthUOM)();
            // return lengthText(effective, pierSettings.DefaultLengthUOM)();
        },
        TransitTime: transitTime,
        ToggleStartConfirmed,
        DeepDraftConfirmed: deepDraftConfirmed,
        ToggleDeepDraftConfirmed,
        HasDraftConflict: hasDraftConflict,
        EffectiveDeepDraft: effectiveDeepDraft,
        EffectiveAirDraft: effectiveAirDraft,
        SyncedStartAndEndEstimate: syncedStartAndEndEstimate,
        ShowUndoStartActual: showUndoStartActual,
        UndoStartActual,
        ShowUndoEndActual: showUndoEndActual,
        UndoEndActual,
        ShowEditMovementEndPopup: showEditMovementEndPopup,
        EditMovementEndPopup: editMovementEndPopup,
        ShowDateTimeEditPopup: showDateTimeEditPopup,
        DateTimeEditPopup: dateTimeEditPopup,
        ShowLogActualTimePopup: showLogActualTimePopup,
        LogActualTimePopup: logActualTimePopup,
        ShowNullableDateTimeEditPopup: showNullableDateTimeEditPopup,
        NullableDateTimeEditPopup: nullableDateTimeEditPopup,
        EffectiveUserPreferences: draftPreferences,
        ToggleRemarksVisible: () => draftPreferences.RemarksVisible(!draftPreferences.RemarksVisible()),
        Acknowledge,
        PromptForCancellationRemarks,
        CancellationRemarksPrompt: cancellationRemarksPrompt,
        PromptForAbortRemarks,
        AbortRemarksPrompt: abortRemarksPrompt,
        CancelWholePortCallPrompt: cancelWholePortCallPrompt,
        Reorder: onReorder,
        Delete,
        Working,
        ShowAddShift: showAddShift,
        AddShiftPopup: addShiftPopup
    };

    function ToggleStartConfirmed() {
        draft.StartTimeConfirmed(!draft.StartTimeConfirmed());
    }

    function ToggleDeepDraftConfirmed() {
        deepDraftConfirmed(!deepDraftConfirmed());
    }

    function UndoStartActual() {
        S.freeze(() => {
            draft.Start.Actual(null);
            showUndoStartActual(false);
        });
    }

    function UndoEndActual() {
        S.freeze(() => {
            draft.End.Actual(null);
            showUndoEndActual(false);
        });
    }

    function Acknowledge() {
        // TH 04/14/2017 -- UPDATE:
        // Now batching PJO Acknowledgement request to prevent multiple overlapping requests from
        // crashing the server.
        // So no longer using the AutoSavingDraftObject to persist AcknowledgedDate changes.
        onAcknowledge();

        // TH 04/12/2017 -- Hmm...
        // Subtle bug. Setting the AcknowledgedDate and calling onAcknowledge in the same tick
        // ends up preventing the autosave from happening.
        // That's b/c the PilotJobOrderDetailsViewModel is constructed inside an S computation
        // in the DispatchDashboardViewModel that depends on the selectedItemKey, and onAcknowledge
        // changes the selectedItemKey.
        // Will probably look at splitting up our draft construction so that our draft objects
        // remain stable across changing the selected PJO.
        // But for now removing the freeze is enough to get this saving correctly.
        // S.freeze(() => {
            // draft.AcknowledgedDate(tzutils.now());
            // onAcknowledge();
        // });
    }

    function PromptForAbortRemarks() {
        if (!Working()) showAbortRemarksPromp(true);
    }

    function PromptForCancellationRemarks() {
        if (!Working()) showCancellationRemarksPromp(true);
    }

    function Cancel(remarks : string | null) {
        if (Working()) return;
        Working(true);
        const req = cnxClient.Post({ Id: 0, Remarks: remarks || null });
        return req.then(result => S.freeze(() => {
            data.PilotJobOrderCancellations.update1(req.Date, result.Cancellation!);
            if (result.AllPilotJobOrdersCancelled) cancelWholePortCallParams({ remarks });
        })).finally(() => Working(false));
    }

    function Delete() {
        if (Working()) return;
        S.freeze(() => {
            actionToConfirm(null);
            Working(true);
        });
        const req = client.Delete();
        req.then(() => S.freeze(() => {
            data.PilotJobOrders.update(req.Date, [], [pjo.Id()]);
            onDelete();
        })).finally(() => Working(false));
    }

    function AddShiftSuccess(entities : DataCacheUpdateEntities) {
        // TH 02/13/2017 -- We expect 2 PJOs to be added/updated from the Add Shift:
        //   1) The current PJO, which will change from an OUT to a SHIFT
        //   2) The new OUT PJO
        // We wanna pass the new OUT to the DispatchDashboard, so we filter out the current PJO.
        const _pjo = entities.PilotEntities()?.PilotJobOrders().filter(p => p !== pjo)[0] as PilotJobOrder | undefined;
        if (_pjo) onAddShift(_pjo);
        else throw new Error('AddShiftSuccess -- Expected to find a new PilotJobOrder.');
    }
}
export type PilotJobOrderViewModel = ReturnType<typeof PilotJobOrderViewModel>;


function EffectiveDraftViewModel(
    pjoRawDraft : NullableMeasurement,
    pjoEffectiveDraft : EffectiveDraft
) {
    // TH 02/03/2017 -- Unlike the effective drafts from data/models/PilotJobOrder.ts,
    // this version also checks a hasWritten flag. The purpose is to allow users to
    // temporarily clear the display value of the field.
    const hasWritten = S.data(false),
        effective = () => hasWritten() ? pjoRawDraft : pjoEffectiveDraft,
        backingMeasurement = {
            Num: (val? : number | null) => {
                if (val === undefined) {
                    return effective().Num();
                }
                return S.freeze(() => {
                    hasWritten(true);
                    return pjoEffectiveDraft.Num(val);
                });
            },
            UOM: () => effective().UOM()
        },
        decimalPlaces = { inches: 0, meters: 2 },
        feetInchesMeters = NullableFeetInchesMetersEdit({ backingMeasurement, decimalPlaces });

    return {
        FeetInchesMeters: feetInchesMeters,
        Lock: pjoEffectiveDraft.Lock,
        OnBlur: () => hasWritten(false),
        HasLockedValue: () => pjoRawDraft.Num() !== null
    };
}
export type EffectiveDraftViewModel = ReturnType<typeof EffectiveDraftViewModel>;

function ItineraryPointViewModel(
    point : ItineraryPoint,
    ajax : IAjax,
    data : PCCache,
    berths : ArraySignal<Berth>,
    reservationModel : (br : BerthReservation) => ReservationViewModel
) {
    const client = ajax.Url<ItineraryPoint>(() => point.Url() || '', { useCache: true, include: [ pt => pt.BerthReservation ] }),
        { object: draft, methods: draftMethods } = DraftObject(point, [
            // aah: itinerary time updates is now handled server-side
            //'Arrival.Estimated',
            //'Arrival.Actual',
            //'Departure.Estimated',
            //'Departure.Actual',
            'LocationId'
        ]),
        berthQuery = S.data(S.sample(() => point.Location()?.Name() ?? '')),
        berthTypeahead = new Typeahead<Berth>({
            typeaheadOptions: {
                minLength: 0,
                highlight: true,
                hint: false
            },
            collection: berths,
            observable: berthQuery,
            matchFn: (berth, query) => !!berth.Location() &&
                CaseInsensitiveStartsWith(berth.Location()!.Name(), query),
            displayKey: berth => berth.Location() && berth.Location()!.Name() || '',
            onSelect: berth => draft.LocationId(berth.Id())
        }),
        berthReservation = S(() => point.BerthReservation() && reservationModel(point.BerthReservation()!)),
        mergedDraftMethods = mergeDraftMethods(draftMethods, () => berthReservation()?.DraftMethods ?? null),
        Validation = ValidationWatcher(client);

    DraftAutoSaver({ object: draft, methods: mergedDraftMethods }, client);

    draft.Location = S.value(S.sample(point.Location));
    data.ItineraryPoints.attach(draft, [draft.Location]);
    draft.BerthReservation = () => berthReservation()?.Model ?? null;

    S(() => draft.Location() && berthQuery(draft.Location()!.Name()));

    // when location changes, check that res footmarks are still in allowed range for given berth and move res to start of berth if not
    // DC 11/04/2024 -- I think the footmarks bug is related to this... Below occurs when it zero's out, and so I think it's relocating it?
    // The main issue is coming from somewheres else, i.e. new package changes, S-js invocation, etc., that causes the cache to temporarily
    // reset / clear OR something else I have no idea. It triggers this code to run, which sees the berth and draft as being different, and
    // it resets the footmarks. By checking that the draft and berth are the same, we can avoid the zero-ing out while still allowing berth
    // swaps to occur. 
    S.on(draft.Location, () => {
        const b = draft.Location()?.Berth(),
            res = berthReservation();

        if (!b || !res) return;

        // DC 11/05/2024 -- Check that the locations match to avoid resetting the footmarks if they do.
        if (b.Location()?.Name() !== res.Model.Berth()?.Location()?.Name()) {
            res.Model.Footmark.Num(Math.ceil(b.StartMark.Num()));
        } 
    }, null, true);

    return {
        Model: draft,
        BerthReservation: berthReservation,
        BerthTypeahead: berthTypeahead,
        Validation
    };
}
export type ItineraryPointViewModel = ReturnType<typeof ItineraryPointViewModel>;

function PortCallViewModel(
    portcall : PortCall,
    ajax : IAjax,
    agentModel : (u : PCUser) => AgentViewModel
) {
    const client = ajax.Url<PortCall>(() => portcall.Url() || ''),
        draft = AutoSavingDraftObject(portcall, client, [ 'NextPortOfCall', 'LastPortOfCall' ]);

    return {
        Model: draft,
        Agent: S(() => portcall.Agent() && agentModel(portcall.Agent()!)),
        Unlisted: S(() => portcall.Unlisted())
    };
}
export type PortCallViewModel = ReturnType<typeof PortCallViewModel>;

function VesselViewModel(
    vessel : Vessel,
    ajax : IAjax,
    pierSettings : PierSettings,
    bowThrusterModel : (bowThruster : BowThruster) => BowThrusterViewModel
) {
    const client = ajax.Url<Vessel>(() => vessel.Url() || '', { useCache: true }),
        // TH 02/05/2017 -- TODO: For incorporating BowThruster into Lloyd's --
        // That'll mean users no longer edit vessel.BowThruster directly but
        // instead make they're edits to the OverrideServiceVessel.
        // So we need to rewire our AutoSavingDraftObjects.
        draft = AutoSavingDraftObject(vessel, client, [
            'BowThruster.Present',
            'BowThruster.Size.Num',
            'BowThruster.Size.UOM'
        ]),
        pilotShiphandlingNotes = vessel.PilotShiphandlingNotes
            // Reverse chronological
            .orderBy(n => n.Event() ? -n.Event()!.Date() : 0),
        shiphandlingNotesClient = ajax.Url<PilotShiphandlingNote>(() => vessel.PilotShiphandlingNotesUrl() || '', {
            useCache: true
        }),
        draftNote = S.data(new PilotShiphandlingNote()),
        bowThruster = bowThrusterModel(draft.BowThruster),
        addingNote = S.data(false),
        clearingNote = S.data(false);

    return {
        Model: draft,
        // Length and Beam in feet and inches or in tenths of a meter.
        Beam: lengthText(draft.Beam, pierSettings.DefaultLengthUOM),
        PilotShiphandlingNotes: pilotShiphandlingNotes,
        DraftNote: draftNote,
        AddShiphandlingNote,
        ClearShiphandlingNote,
        BowThruster: bowThruster,
    };

    function ClearShiphandlingNote(note : PilotShiphandlingNote) {
        // DC 10/08/2024 -- This method on the server causes some missing / misaligned timestamps (via middleware?) when we get back 
        // here to the frontend, i.e. bluebird and failed promises. However, the server response is 200 OK and the data persists as expected.
        // Rough solution is to force a page reload to get the newest data, i.e. the UX is right, but there may be issues "downstream"?
        clearingNote(true);
        shiphandlingNotesClient.Post(note)
            .then(() => clearingNote(false))
            .finally(() => location.reload());
            // .error((err) => console.log(err));
    }

    function AddShiphandlingNote() {
        // Prevent accidental empty notes.
        if (!draftNote().Message() || !draftNote().Message()!.trim() || addingNote()) return;
        addingNote(true);
        shiphandlingNotesClient.Post(draftNote())
            .then(() => draftNote(new PilotShiphandlingNote()))
            .finally(() => addingNote(false));
    }
}
export type VesselViewModel = ReturnType<typeof VesselViewModel>;

function BowThrusterViewModel(
    bowThruster : BowThruster,
    model : ServerPilotJobOrderDetails
) {
    const sizeConvertible  = new NullableConvertibleMeasurement(bowThruster.Size),
        sizeInHorsepower = new ConvertedMeasurement(sizeConvertible, UOM.Horsepower, 0),
        sizeInKilowatts  = new ConvertedMeasurement(sizeConvertible, UOM.Kilowatt, 0);

    // TH 02/05/2017 -- Coerce BowThruster UOM to Horsepower or Kilowatt.
    // This triggers an auto-save request, hence why we need to check permissions.
    //
    // Before typescript conversion, we used to do this via a KO options binding in which
    // the only available options were Horsepower and Kilowatt.
    // But KO and React have different behaviors around <select> elements when the current value
    // is not in the list of available <option>'s --
    // KO would set the current value to the first available <option>, if any.
    // Whereas React silently leaves the current value as is, even though the UI looks as if
    // the first available option is selected.
    // Personally, this feels like a bug on React's part, since React is normally quite
    // strict about avoiding cases where visible view state and js model state differ.
    S(() =>
        bowThruster.Present() &&
        bowThruster.Size.UOM() !== UOM.Horsepower &&
        bowThruster.Size.UOM() !== UOM.Kilowatt &&
        model.CanEditVessel() &&
        bowThruster.Size.UOM(UOM.Horsepower)
    );

    return {
        Raw: bowThruster,
        SizeInHorsepower: sizeInHorsepower,
        SizeInKilowatts: sizeInKilowatts,
        OnSizeBlur: () => sizeConvertible.LastWriter(null)
    };
}
export type BowThrusterViewModel = ReturnType<typeof BowThrusterViewModel>;

function AgentViewModel(user : PCUser) {
    const mobileNumbers = user.PhoneNumbers.filter(pn => pn.Kind() === PhoneNumberKind.Mobile),
        // Logic for "main" phone number taken from personprofilepopup
        mainNumber = S(() =>
            mobileNumbers().length > 0 ? mobileNumbers()[0] : user.PhoneNumbers().length > 0 ? user.PhoneNumbers()[0] : null);

    return {
        Model: user,
        MainNumber: mainNumber
    };
}
export type AgentViewModel = ReturnType<typeof AgentViewModel>;

function ReservationViewModel(
    berthReservation : BerthReservation,
    canEdit : () => boolean,
    footmarkAssignmentsModel : (params : FootmarkAssignmentsParams) => FootmarkAssignments
) {
    const
        // Distinguish different "tiers" of draft objects...
        // ...starting with Length.Num in a tier all by itself, b/c it doesn't need to get
        // saved (the server applies its own logic for computing it), but we still wanna
        // distinguish the draft value from the persisted value...
        draftForLength = DraftObject(berthReservation, [
            'Length.Num'
        ]),
        // ...and then those properties that we'll auto-save with our ItineraryPoint.
        draft = DraftObject(draftForLength.object, [
            'Footmark.Num',
            'Footmark.UOM',
            'SideTo'
        ]);

    // Keep reservation length up-to-date when changing side to. This is purely for client-side
    // purposes (fast feedback when making changes) -- the server applies its own version of
    // the same logic.
    S(isInitialRun => {
        const portcall = berthReservation.Itinerary()?.PortCall();
        // Propagate portcall changes to non-cancelled reservations only, but update for SideTo
        // changes whether or not the reservation is cancelled.
        if (portcall && (berthReservation.CancelledDate() === null || draft.object.SideTo() !== berthReservation.SideTo())) {
            const newLength = BerthReservation.calculateLength(
                portcall.Vessel()?.Length ?? null,
                portcall.Vessel()?.Beam ?? null,
                portcall.LengthOnlyLength,
                draft.object.SideTo(),
                berthReservation.Length.UOM()
            );
            // Apply new length so long as we computed a usable value, but only if this is a
            // follow up run of this computation (due to data changing). Otherwise, leave
            // existing length as is whether it agrees w/ our computed length or not.
            if (newLength?.Num() && !isInitialRun) draft.object.Length.set(newLength as Measurement);
        }
        return false;
    }, true);

    // Revert draft Length.Num changes when we've persisted all pending changes to this
    // reservation, as at that point the server should have calculated the new length itself
    // and we should have applied that value to our data cache.
    S(() => {
        if (draftForLength.methods.isCaching() && !draft.methods.isCaching()) {
            draftForLength.methods.checkpoint().revert();
        }
    });

    return {
        Model: draft.object,
        DraftMethods: draft.methods,
        FootmarkAssignments: footmarkAssignmentsModel({
            Reservation: draft.object,
            CanEdit: canEdit,
            // B/c we auto-save footmarks edits, don't write them to our draft Reservation until the user blurs
            // either the startmark or endmark input.
            FootmarksUpdateTrigger: FootmarksUpdateTrigger.OnBlur
        })
    };
}
export type ReservationViewModel = ReturnType<typeof ReservationViewModel>;

function BerthReservationDetailsSection(jobOrder : PilotJobOrderViewModel) {
    const userSelectedItineraryPoint = S.data<ItineraryPointViewModel | null>(null);

    return {
        SelectedItineraryPoint: ((pt? : ItineraryPointViewModel | null) => {
            if (pt === undefined) {
                const
                    fromRes = jobOrder.FromPoint()?.BerthReservation(),
                    toRes   = jobOrder.ToPoint  ()?.BerthReservation(),
                    userSelection = userSelectedItineraryPoint();

                return fromRes && toRes && userSelection ? userSelection
                    : fromRes ? jobOrder.FromPoint()!
                    : toRes ? jobOrder.ToPoint()!
                    : null;
            }
            return userSelectedItineraryPoint(pt);
        }) as DataSignal<ItineraryPointViewModel | null>
    };
}
export type BerthReservationDetailsSection = ReturnType<typeof BerthReservationDetailsSection>;

export type CancellationRemarksPrompt = ReturnType<typeof CancellationRemarksPrompt>;
function CancellationRemarksPrompt(
    doCancel : (remarks : string | null) => PromiseLike<void> | undefined,
    onDismiss : () => void
) {
    const Remarks = S.value<string | null>(null);

    return {
        Remarks,
        CancelPJO,
        QuitChanges: onDismiss
    };

    function CancelPJO() {
        onDismiss();
        doCancel(Remarks());
    }
}

export type AbortRemarksPrompt = ReturnType<typeof AbortRemarksPrompt>;
function AbortRemarksPrompt(
    doAbort : (remarks : string | null) => PromiseLike<void> | undefined,
    undoActual : () => void,
    onDismiss : () => void
) {
    const Remarks = S.value<string | null>(null);

    return {
        Remarks,
        AbortPJO,
        QuitChanges: onDismiss
    };

    function AbortPJO() {
        undoActual();
        // DC 02/08/2022 -- Required timeout to allow undoing the actual to propogate thru and save via DB and/or cache
        // Will consider revisiting for a more elegant S solution in the future
        setTimeout(() => {
            onDismiss();
            doAbort(Remarks()); 
        }, 500);
    }
}

export const enum CancelWholePortCallStatus {
    Prompting,
    Cancelling,
    CancellationSucceeded,
    CancellationFailed
}

export type CancelWholePortCallPrompt = ReturnType<typeof CancelWholePortCallPrompt>;
function CancelWholePortCallPrompt({
    pjo,
    remarks,
    ajax,
    entrypoints,
    hidePrompt
} : {
    pjo : PilotJobOrder,
    remarks : string | null,
    ajax : IAjax,
    entrypoints : Entrypoints,
    hidePrompt : () => void
}) {
    const
        url = () => entrypoints.CancelPortCall({ Id: pjo.PortCallId() }),
        client = ajax.Url<CancelPortCallRequestOptions, {}>(url, { useCache: true }),
        status = S.value(CancelWholePortCallStatus.Prompting),
        exception = S.value<any>(null);

    return {
        Status: status,
        ExceptionDetails: S(() => {
            const ex = exception();
            if (ex) {
                const stringified = ex.toString();
                if (stringified === "[object Object]") {
                    try {
                        return JSON.stringify(ex, undefined, 2);
                    } catch (ex2) {
                        return null;
                    }
                } else {
                    return stringified;
                }
            } else {
                return null;
            }
        }),
        CancelWholePortCall,
        HidePrompt: hidePrompt,
        PortCallProfileHref: () => `#!portcallprofile?id=${pjo.PortCallId()}`
    };

    async function CancelWholePortCall() {
        if (status() !== CancelWholePortCallStatus.Prompting) return;
        status(CancelWholePortCallStatus.Cancelling);
        try {
            await client.Post({ Remarks: remarks || null });
            status(CancelWholePortCallStatus.CancellationSucceeded);
        } catch (ex) {
            status(CancelWholePortCallStatus.CancellationFailed);
            exception(ex);
        }
    }
}

function lengthText(length : NullableMeasurement, uom : () => string | null) {
    const effectiveUom = () => uom() || UOM.Foot;
    return () => effectiveUom() === UOM.Foot ?
        new MeasurementInFeetAndInches(length, 0).toString() :
        new ConvertedMeasurement(length, effectiveUom(), 1).toString();
}



// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/model.ts